import { OFFERED_INTENTS, type AskIntentKey } from './wiki'
import { pageKeyFor } from './askOnePage'

/**
 * PREFILLED LINKS INTO HELP (31 Oct, HLP-44; One and Help v2 S2).
 *
 * Ask One sends nothing to anyone. Where it used to (HLP-41's one-tap "Send to
 * platform support", the routing step after a thumbs-down), it now offers a
 * LINK that opens Help's form with the person's words already in it. The
 * person reads the form, can change every word and the team, and presses Send
 * themselves - or doesn't. "Nothing leaves One without a form the person
 * opened" is this file plus the absence of any send in AskOneDrawer.tsx.
 *
 *   #/help?new=ticket&lane=technical&q=<words>&from=<log ref>&page=#/stars
 *   #/help?new=feedback&q=<words>&from=<log ref>&page=#/stars
 *
 * ── A LINK IS A REQUEST, NOT A PERMISSION (standing rule 20) ────────────────
 *
 * Anybody can type a URL, and a colleague can paste one into a post. So every
 * part is re-read here and anything unexpected is dropped rather than obeyed:
 *  - `new` is ticket or feedback, else no dialog opens;
 *  - `lane` must be a lane the ticket form OFFERS - `OFFERED_INTENTS`, which
 *    is `intentRouting` with HR's hold already applied. `lane=hr` therefore
 *    falls back to Platform support (D2's default); it can never preselect a
 *    held lane, and `intentRouting` would refuse the send anyway;
 *  - `q` is capped at the form's own limit and is only ever a field's VALUE;
 *  - `from` must look like a log row id; stamp_ask_one checks the rest;
 *  - `page` (S3, 31 Oct) is the route the person was on when One offered the
 *    link, so the ticket's page details name THAT page and not #/help (S2's
 *    known gap). Only a `#/` path the app knows - `pageKeyFor`, the
 *    HELP_COVERAGE map - no longer than HELP_PAGE_MAX survives, and its query
 *    is cut off, so a planted link cannot smuggle words into the page
 *    details. Anything else is dropped and the form reads the page it is on.
 * Nothing here sends. The worst a planted link can do is fill a form the
 * reader can see and cancel.
 */

export type HelpOpen = 'ticket' | 'feedback'

/** The lanes a ticket link may name: the ones the ticket form offers. */
export type HelpLane = 'platform' | 'technical'

export type HelpSeed = {
  open: HelpOpen
  /** Ticket only. Absent → Platform support (D2). */
  lane: HelpLane | null
  /** The words that fill the form. Editable; never sent by the link. */
  text: string
  /** The Ask One conversation's log row, so a send still stamps its outcome. */
  from: string | null
  /** The page the person was on (`#/stars`), validated. Null = #/help's own. */
  page: string | null
}

/** The most a link carries - the ticket's detail limit. */
export const HELP_LINK_MAX = 2000

/** The longest page path a link carries. */
export const HELP_PAGE_MAX = 200

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** A known `#/` route with its query cut off, or null. Both ends use it. */
export function cleanHelpPage(raw: string | null | undefined): string | null {
  if (!raw) return null
  const path = raw.split('?')[0]
  if (path.length > HELP_PAGE_MAX) return null
  if (!/^#\/[A-Za-z0-9/_-]*$/.test(path)) return null
  return pageKeyFor(path.slice(1)) === null ? null : path
}

/** Where the person is now, for a link built on this page. */
export function currentHelpPage(): string | null {
  return typeof window === 'undefined' ? null : cleanHelpPage(window.location.hash)
}

/** A ticket's page details: the page a link names, if it is still a known
 *  route, in place of the page the form was opened on. Everything else read
 *  from the browser (viewport, version, locale) is unchanged - it is true of
 *  the person now. */
export function seededPageContext<T extends { route: string }>(here: T, page: string | null | undefined): T {
  const p = cleanHelpPage(page)
  return p ? { ...here, route: p } : here
}

/** Lanes the ticket form offers, read from the one list (rule 11). */
function offeredLanes(): string[] {
  return OFFERED_INTENTS.flatMap((i) => (i.lane ? [i.lane] : []))
}

/** The link. `#/help` alone when there is nothing to prefill. */
export function helpHref(seed?: Partial<HelpSeed> & { open: HelpOpen }): string {
  if (!seed) return '#/help'
  const p = new URLSearchParams()
  p.set('new', seed.open)
  if (seed.open === 'ticket' && seed.lane) p.set('lane', seed.lane)
  const text = (seed.text ?? '').trim().slice(0, HELP_LINK_MAX)
  if (text) p.set('q', text)
  if (seed.from && UUID.test(seed.from)) p.set('from', seed.from)
  const page = cleanHelpPage(seed.page)
  if (page) p.set('page', page)
  return `#/help?${p.toString()}`
}

/** What #/help does with its URL. Null = open nothing. */
export function parseHelpSeed(params: URLSearchParams): HelpSeed | null {
  const open = params.get('new')
  if (open !== 'ticket' && open !== 'feedback') return null
  const lane = params.get('lane')
  const from = params.get('from')
  return {
    open,
    lane:
      open === 'ticket' && lane && offeredLanes().includes(lane)
        ? (lane as HelpLane)
        : null,
    text: (params.get('q') ?? '').slice(0, HELP_LINK_MAX),
    from: from && UUID.test(from) ? from : null,
    page: cleanHelpPage(params.get('page')),
  }
}

/** The intent key the ticket form starts on. */
export function seedIntent(seed: HelpSeed | null): AskIntentKey {
  return seed?.lane ?? 'platform'
}
